import apiClient from '../../../services/apiClient';

// Client for the context-aware AI tutor (POST /api/v1/ai/tutor). Wraps the seven
// tutor modes; answers are grounded in the current slide + retrieved lecture
// passages on the backend (LectureRagService.tutor).

export type TutorMode =
  | 'explain' // explain this slide at intro level
  | 'deeper' // a more rigorous treatment
  | 'eli5' // explain as simply as possible
  | 'example' // one fully worked example
  | 'quiz' // 1–2 check-for-understanding questions
  | 'check' // assess the student's attempt, or pose probing questions
  | 'chat'; // free-form follow-up grounded in the lesson

export interface TutorRequest {
  lessonId: string;
  slideIndex: number;
  mode: TutorMode;
  // Required for 'chat'; optional context for 'check'.
  message?: string;
  // Optional model override (model_name from acechem_ai_models). Omit to use the
  // DB registry default — the funnel is DB-driven, never hardcode an id here.
  model?: string;
}

// Difficulty tier the tutor assigned a generated question (Bloom-style ladder).
export type TutorQuestionDifficulty = 'recall' | 'apply' | 'analyze';

// A single mini-quiz item: a question and its model answer (revealed on demand).
//
// A LIVE model tends to fold its rationale into `a`. An AUTHORED set keeps them
// apart — a complete answer, and one separate sentence of why. The panel now
// renders `explanation` under the answer when `a` does not already contain it
// (see showWhy in TutorPanel): before that it rendered nothing, and every
// authored rationale existed only for the marking prompt.
//
// `difficulty` is still additive and unrendered — it orders an authored pool
// easiest-first, which the rotation preserves.
export interface TutorQuestion {
  q: string;
  a: string;
  // Additive: one-line rationale for the correct answer (also embedded in `a`).
  explanation?: string;
  // Additive: difficulty tier of this question.
  difficulty?: TutorQuestionDifficulty;
}

export interface TutorResponse {
  // Prose answer (empty when the response is a structured mini-quiz instead).
  // When `html` is present this is its plain-text flattening, derived server-side.
  text: string;
  // Rich cached HTML (BP 11). Present only for a mode chip pressed with no typed
  // message in one of the four cacheable modes — explain / deeper / eli5 /
  // example — which every student on the course asks identically, so the answer
  // is generated once and reused.
  //
  // UNTRUSTED: it is model output, and the slide's own text was part of the
  // prompt that produced it. Render it ONLY through <RichExplanation>, which
  // sanitises with DOMPurify; never hand it to dangerouslySetInnerHTML directly.
  html?: string;
  format?: 'html' | 'markdown';
  // True when served from acechem_ai_explanations rather than generated now.
  cached?: boolean;
  // Present for quiz/check modes when the model returned structured questions.
  questions?: TutorQuestion[];
  // Ids of the retrieved chunks that grounded the answer (RAG citations).
  citations?: string[];
  mode: TutorMode;
  slideId?: string;
  model?: string;
  // Additive: the adapted difficulty level (0 simplest … 3 most rigorous) the
  // tutor settled on for this turn, after factoring in the student's message.
  level?: number;
}

// Ask the tutor. The backend grounds the response in the current slide and
// related lecture passages, then acts per `mode`.
export async function askTutor(req: TutorRequest): Promise<TutorResponse> {
  const { data } = await apiClient.post<TutorResponse>('/api/v1/ai/tutor', req);
  return {
    text: data.text ?? '',
    html: data.html,
    format: data.format,
    cached: data.cached,
    questions: data.questions,
    citations: data.citations ?? [],
    mode: data.mode ?? req.mode,
    slideId: data.slideId,
    model: data.model,
    level: data.level,
  };
}
